import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { formatDate, formatShortName, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Combobox, Eyebrow, Select, Skeleton, useToast } from '../../ds/components';
import { useEscapeKey, useFocusTrap, useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions, useWorkspace } from '../../lib/session';
import { CalendarPage } from './CalendarModule';
import { ConflictView } from './ConflictView';
import { SlotPicker } from './SlotPicker';
import { WeekGrid } from './WeekGrid';
import { useCalendarWeek } from './calendarApi';
import {
  readConflictDetails,
  useContactRecord,
  useContactSearch,
  useCreateAppointment,
  useEventTypes,
  type ConflictDetails,
  type ContactLinkedItem,
  type EventType,
  type Slot,
} from './api';
import { formatPanelTitle, formatTimeRange, startOfWeek, todayKey, weekdayShort } from './dates';
import fields from './fields.module.css';
import styles from './AppointmentPanelPage.module.css';

/** The hint of Beratung 02 — only for a video type whose option „Aufzeichnung und Transkript" is on (D-163). */
const RECORDING_TEXT =
  'Aufzeichnung und Transkript laufen bei jedem Videotermin. Der Mandant bestätigt die vier Bedingungen in der Lobby, sonst kann er nicht beitreten.';

interface PanelState {
  from?: string;
}

function stateOf(value: unknown): PanelState {
  return typeof value === 'object' && value !== null ? value : {};
}

/** „Erstberatung · Videoberatung, 45 Min" — the option text of the type select. */
export function eventTypeOptionLabel(type: EventType): string {
  return `${type.name} · ${type.channelLabel}, ${type.durationMinutes} Min`;
}

function slotFrom(
  startsAt: string | null,
  type: EventType | null,
  advisorId: string | undefined,
): Slot | null {
  if (!startsAt || !type) return null;
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return null;
  return {
    startsAt: start.toISOString(),
    endsAt: new Date(start.getTime() + type.durationMinutes * 60_000).toISOString(),
    advisorId: advisorId ?? '',
  };
}

/** Beratung 02 — „Termin anlegen" as a slide-in panel over the (dimmed) calendar; 409 → Beratung 03. */
export function AppointmentPanelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const workspace = useWorkspace();
  const permissions = usePermissions();
  const mobile = useIsMobile();
  const { toast } = useToast();
  const panelRef = useRef<HTMLDivElement>(null);
  const types = useEventTypes();
  const create = useCreateAppointment();

  const [typeId, setTypeId] = useState<string>(params.get('eventTypeId') ?? '');
  const [contactId, setContactId] = useState<string | null>(params.get('contactId'));
  const [query, setQuery] = useState('');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dealId, setDealId] = useState<string | null>(params.get('dealId'));
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const presetStart = params.get('startsAt');
  const completesTaskId = params.get('taskId');
  // The dimmed week behind the panel: the week of the preselected time (Beratung 02).
  const background = useCalendarWeek(
    startOfWeek(
      presetStart && !Number.isNaN(Date.parse(presetStart)) ? toBerlinDateKey(presetStart) : todayKey(),
    ),
    null,
  );

  const activeTypes = useMemo(() => (types.data ?? []).filter((type) => type.active), [types.data]);
  const type = activeTypes.find((entry) => entry.id === typeId) ?? null;
  // The panel books the acting user's own calendar (the host); the type's rotation is the public page's business.
  const advisor = { advisorId: workspace.user.id };
  const me = formatShortName(workspace.user.firstName, workspace.user.lastName);

  useEffect(() => {
    if (!typeId && activeTypes.length > 0) setTypeId(activeTypes[0]?.id ?? '');
  }, [activeTypes, typeId]);

  useEffect(() => {
    if (type && !slot && presetStart) setSlot(slotFrom(presetStart, type, advisor.advisorId));
    // The preset applies once, when the type is known.
  }, [type?.id]);

  const search = useContactSearch(query);
  const record = useContactRecord(contactId);
  const contactName = record.data?.header.name ?? '';
  const contactShortName = record.data
    ? formatShortName(record.data.header.firstName, record.data.header.lastName)
    : '';
  const deals: ContactLinkedItem[] = useMemo(
    () => record.data?.linked.find((group) => group.key === 'deals')?.items ?? [],
    [record.data],
  );
  const deal = deals.find((item) => item.id === dealId) ?? deals[0] ?? null;

  useEffect(() => {
    if (deal && deal.id !== dealId) setDealId(deal.id);
  }, [deal, dealId]);

  const close = () => {
    const state = stateOf(location.state);
    if (state.from) {
      void navigate(state.from);
    } else if (location.key !== 'default') {
      void navigate(-1);
    } else {
      void navigate('/calendar');
    }
  };

  useFocusTrap(panelRef, conflict === null, { initialFocus: 'container' });
  useEscapeKey(close, conflict === null && !pickerOpen);

  const options = useMemo(
    () =>
      (search.data?.items ?? []).map((item) => ({
        id: item.id,
        label: `${item.firstName} ${item.lastName}`.trim(),
        description: [item.lifecycle.label, item.deal, item.owner?.name].filter(Boolean).join(' · '),
        disabled: item.dnc,
      })),
    [search.data],
  );

  async function book(startsAt: string, overrideConflict = false) {
    if (!type || !contactId) return;
    setFormError(null);
    setConflictError(null);
    try {
      const created = await create.mutateAsync({
        eventTypeId: type.id,
        contactId,
        advisorId: advisor.advisorId,
        startsAt,
        ...(deal ? { dealId: deal.id } : {}),
        ...(completesTaskId ? { completesTaskId } : {}),
        ...(overrideConflict ? { overrideConflict: true } : {}),
      });
      toast({ message: 'Termin angelegt und Einladung versendet.' });
      await navigate(`/calendar/appointments/${created.id}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const details = readConflictDetails(error.details);
        if (details) {
          setConflict(details);
          return;
        }
      }
      if (error instanceof ApiError && error.status === 403 && conflict) {
        setConflictError(error.detailString('helper') ?? error.message);
        return;
      }
      const message =
        error instanceof ApiError ? (Object.values(error.fields)[0] ?? error.message) : errorMessage(error);
      if (conflict) setConflictError(message);
      else setFormError(message);
    }
  }

  function submit() {
    if (!type) return;
    if (!contactId) {
      setFormError('Bitte einen Kontakt wählen.');
      return;
    }
    if (!slot) {
      setFormError('Bitte eine Zeit wählen.');
      setPickerOpen(true);
      return;
    }
    void book(slot.startsAt);
  }

  if (conflict && type) {
    return (
      <ConflictView
        details={conflict}
        type={type}
        contactName={contactShortName || 'Kontakt'}
        canOverride={permissions.is('settings.organization', 'yes')}
        pending={create.isPending}
        error={conflictError}
        onPick={(picked) => {
          setSlot(picked);
          void book(picked.startsAt);
        }}
        onOverride={() => void book(conflict.requested.startsAt, true)}
        onBack={() => {
          setConflict(null);
          setConflictError(null);
        }}
      />
    );
  }

  const title = slot ? formatPanelTitle(slot.startsAt) : 'Zeit wählen';
  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-panel-title"
      tabIndex={-1}
      className={styles.panel}
    >
      <div className={styles.head}>
        <div>
          <Eyebrow>Neuer Termin</Eyebrow>
          <div id="appointment-panel-title" className={styles.title}>
            {title}
          </div>
        </div>
        <button type="button" className={styles.close} aria-label="Schließen" onClick={close}>
          ✕
        </button>
      </div>

      {types.isError ? <Banner tone="danger">{errorMessage(types.error)}</Banner> : null}
      {types.isPending ? (
        <div className={styles.skeletons} aria-busy="true">
          <Skeleton height={37} />
          <Skeleton height={37} />
          <Skeleton height={120} />
        </div>
      ) : null}

      {type ? (
        <>
          <Select
            label="Ereignistyp"
            className={fields.field}
            value={typeId}
            options={activeTypes.map((entry) => ({ value: entry.id, label: eventTypeOptionLabel(entry) }))}
            onChange={(event) => {
              setTypeId(event.target.value);
              setSlot(null);
            }}
          />

          <div>
            <span className={fields.label} id="participants-label">
              Teilnehmende
            </span>
            <div className={styles.pills} aria-labelledby="participants-label">
              {contactId ? (
                <span className={clsx(styles.pill, styles.pillContact)}>
                  {record.isPending ? <Skeleton width={90} /> : contactName || 'Kontakt'}
                  <button
                    type="button"
                    className={styles.pillRemove}
                    aria-label={`${contactName || 'Kontakt'} entfernen`}
                    onClick={() => {
                      setContactId(null);
                      setDealId(null);
                      setQuery('');
                    }}
                  >
                    ✕
                  </button>
                </span>
              ) : null}
              <span className={clsx(styles.pill, styles.pillHost)}>{me} · Gastgeber</span>
              {!contactId ? <span className={clsx(styles.pill, styles.pillAdd)}>+ Person</span> : null}
            </div>
            {!contactId ? (
              <div className={styles.combobox}>
                <Combobox
                  label="Kontakt"
                  labelHidden
                  placeholder="Kontakt suchen"
                  className={fields.field}
                  options={options}
                  value={contactId}
                  filter={false}
                  emptyText={query.trim() ? 'Keine Treffer' : 'Name, Telefon oder E-Mail eingeben'}
                  onQueryChange={setQuery}
                  onChange={(id) => {
                    setContactId(id);
                    setFormError(null);
                  }}
                />
              </div>
            ) : null}
            {record.isError ? <Banner tone="danger">{errorMessage(record.error)}</Banner> : null}
            {record.data && !record.data.tools['appointment']?.enabled ? (
              <Banner tone="warning">
                {record.data.tools['appointment']?.reason ??
                  'Für diesen Kontakt kann kein Termin angelegt werden.'}
              </Banner>
            ) : null}
          </div>

          <div>
            <span className={fields.label}>Zeit</span>
            {slot && !pickerOpen ? (
              <div className={styles.timeRow}>
                <span className={clsx(fields.value, styles.timeValue)}>
                  {weekdayShort(slot.startsAt)} {formatDate(slot.startsAt)} ·{' '}
                  {formatTimeRange(slot.startsAt, slot.endsAt)}
                </span>
                <button type="button" className={styles.textAction} onClick={() => setPickerOpen(true)}>
                  Ändern
                </button>
              </div>
            ) : (
              <div className={styles.pickerBox}>
                <SlotPicker
                  eventTypeId={type.id}
                  advisorId={advisor.advisorId}
                  internal
                  layout="compact"
                  value={slot}
                  initialDay={slot ? toBerlinDateKey(slot.startsAt) : undefined}
                  onChange={(picked) => {
                    setSlot(picked);
                    setPickerOpen(false);
                    setFormError(null);
                  }}
                />
              </div>
            )}
          </div>

          <div className={styles.twoColumns}>
            <div>
              <span className={fields.label}>Dauer</span>
              <div className={fields.value}>{type.durationMinutes} Min</div>
            </div>
            <div>
              <span className={fields.label}>Ort</span>
              <div className={fields.value}>{type.locationText ?? type.channelLabel}</div>
            </div>
          </div>

          <div>
            <span className={fields.label}>Bezug</span>
            <div className={styles.reference}>
              {deal ? (
                <>
                  <div className={styles.referenceTitle}>{deal.title}</div>
                  {deal.meta || deal.value ? (
                    <div className={styles.referenceMeta}>
                      {[deal.meta, deal.value].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                  {deals.length > 1 ? (
                    <div className={styles.referenceActions}>
                      <Select
                        label="Bezug wechseln"
                        labelHidden
                        size="compact"
                        value={deal.id}
                        options={deals.map((item) => ({ value: item.id, label: item.title }))}
                        onChange={(event) => setDealId(event.target.value)}
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className={styles.referenceTitle}>
                    {contactId ? `Kontakt · ${contactName || '…'}` : 'Kontakt'}
                  </div>
                  <div className={styles.referenceMeta}>
                    {type.createsDeal
                      ? 'Mit der Buchung entsteht ein Deal für den Kontakt.'
                      : 'Der Termin wird in der Kontakt-Timeline vermerkt.'}
                  </div>
                </>
              )}
            </div>
          </div>

          {type.channel === 'video' && type.recordingEnabled ? (
            <div className={styles.recording}>
              <div className={styles.recordingTitle}>Aufzeichnung ist verpflichtend</div>
              <div className={styles.recordingText}>{RECORDING_TEXT}</div>
            </div>
          ) : null}

          {formError ? <Banner tone="danger">{formError}</Banner> : null}

          <div className={styles.actions}>
            <Button className={styles.primary} loading={create.isPending} onClick={submit}>
              Termin anlegen und einladen
            </Button>
            <Button variant="secondary" className={styles.secondary} onClick={close}>
              Abbrechen
            </Button>
          </div>
        </>
      ) : types.isSuccess && activeTypes.length === 0 ? (
        <Banner tone="neutral">Es gibt noch keinen aktiven Ereignistyp. Legen Sie zuerst einen an.</Banner>
      ) : null}
    </div>
  );

  if (mobile) return <div className={styles.mobileStage}>{panel}</div>;

  return (
    <div className={styles.layout}>
      <div className={styles.background}>
        <CalendarPage tab="week" dimmed flush>
          {background.data ? <WeekGrid week={background.data} /> : <Skeleton height={560} />}
        </CalendarPage>
      </div>
      {panel}
    </div>
  );
}
